/**
 * Schedule Card
 *
 * Glass-morphism card displaying an agent schedule's status, triggers,
 * and run history orbs. Used in the SchedulesPage grid.
 */

import React, { useState } from 'react';
import {
  Play,
  Pause,
  Square,
  Trash2,
  Clock,
  Zap,
  GitBranch,
  Loader2,
  MoreVertical,
  Copy,
  Check,
} from 'lucide-react';
import type { Schedule, RunEntry } from '../services/agentScheduleService';
import RunHistoryOrbs from './RunHistoryOrbs';

// --- Cron human-readable helper ---

function cronToHuman(expr: string | null): string {
  if (!expr) return '';
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) return expr;

  const [min, hour, dom, mon, dow] = parts;

  // Common patterns
  if (min === '*' && hour === '*') return 'Every minute';
  if (hour === '*' && min !== '*') return `Every hour at :${min.padStart(2, '0')}`;

  if (dom === '*' && mon === '*' && dow === '*') {
    if (min !== '*' && hour !== '*') {
      return `Daily at ${hour.padStart(2, '0')}:${min.padStart(2, '0')} UTC`;
    }
    if (hour.includes('/')) {
      const interval = hour.split('/')[1];
      return `Every ${interval} hours`;
    }
    if (hour.includes(',')) {
      return `At ${hour.split(',').map(h => `${h.padStart(2, '0')}:${min.padStart(2, '0')}`).join(', ')} UTC`;
    }
  }

  if (dow !== '*' && dom === '*' && mon === '*') {
    const dayNames: Record<string, string> = {
      '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed',
      '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun',
    };
    const days = dow.split(',').map(d => dayNames[d] || d).join(', ');
    return `${days} at ${hour.padStart(2, '0')}:${min.padStart(2, '0')} UTC`;
  }

  return expr;
}

/** Parse a date string that may be SQLite format (space-separated, no T/Z) */
function parseSqliteDate(dateStr: string): Date {
  let normalized = dateStr;
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T') + 'Z';
  }
  return new Date(normalized);
}

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return 'Never';
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 0) return 'in the future';
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    if (hours < 48) return 'yesterday';
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (days < 60) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return 'Never';
  }
}

function exactDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString();
  } catch {
    return '';
  }
}

function timeUntil(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '--';
    const diff = date.getTime() - Date.now();
    if (diff < 0) return 'overdue';
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return '< 1m';
    if (minutes < 60) return `in ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `in ${hours}h`;
    const days = Math.floor(hours / 24);
    return `in ${days}d`;
  } catch {
    return '--';
  }
}

function getElapsedSeconds(dateStr: string | null | undefined, nowMs: number): number | null {
  if (!dateStr) return null;
  try {
    const startedAt = parseSqliteDate(dateStr);
    if (isNaN(startedAt.getTime())) return null;
    return Math.max(0, Math.floor((nowMs - startedAt.getTime()) / 1000));
  } catch {
    return null;
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function getRunningRun(runs: RunEntry[], currentRunAgentSessionId: string | null): RunEntry | null {
  return runs.find((run) => run.id === currentRunAgentSessionId)
    || runs.find((run) => run.status === 'running')
    || null;
}

function getAverageDurationSeconds(runs: RunEntry[]): number | null {
  const durations = runs
    .map((run) => run.durationSeconds)
    .filter((value): value is number => typeof value === 'number' && value > 0);
  if (durations.length === 0) return null;
  return durations.reduce((sum, value) => sum + value, 0) / durations.length;
}

// --- Status helpers ---

type VisualStatus = 'running' | 'active' | 'disabled';

function getVisualStatus(schedule: Schedule): VisualStatus {
  if (schedule.currentRunAgentSessionId) return 'running';
  if (schedule.enabled) return 'active';
  return 'disabled';
}

function statusDotClass(status: VisualStatus): string {
  switch (status) {
    case 'running':
      return 'bg-[#FFB52B] shadow-[#FFB52B]/50 shadow-lg schedule-pulse-ring';
    case 'active':
      return 'bg-[#FFB52B] shadow-[#FFB52B]/30 shadow-sm';
    case 'disabled':
      return 'bg-gray-400 dark:bg-gray-600';
  }
}

function statusBadge(status: VisualStatus): { label: string; className: string } {
  switch (status) {
    case 'running':
      return {
        label: 'Running',
        className: 'text-[#FFB52B] text-[10px]',
      };
    case 'active':
      return {
        label: 'Active',
        className: 'text-gray-500 dark:text-gray-400 text-[10px]',
      };
    case 'disabled':
      return {
        label: 'Disabled',
        className: 'text-gray-400 dark:text-gray-600 text-[10px]',
      };
  }
}

// --- Props ---

interface ScheduleCardProps {
  schedule: Schedule;
  runs: RunEntry[];
  onRunNow: (id: string) => void;
  onStop: (id: string) => void;
  onToggleEnabled: (id: string, currentlyEnabled: boolean) => void;
  onDelete: (id: string) => void;
  onSelect: (schedule: Schedule) => void;
  onClickRun?: (run: RunEntry) => void;
  isActioning?: boolean;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  schedule,
  runs = [],
  onRunNow,
  onStop,
  onToggleEnabled,
  onDelete,
  onSelect,
  onClickRun,
  isActioning = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Reset stopping state when schedule is no longer running
  const isRunning = !!schedule.currentRunAgentSessionId;
  React.useEffect(() => {
    if (!isRunning) setStopping(false);
  }, [isRunning]);

  React.useEffect(() => {
    if (!isRunning) return undefined;
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [isRunning]);

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    setStopping(true);
    onStop(schedule.id);
  };
  const visualStatus = getVisualStatus(schedule);
  const badge = statusBadge(visualStatus);
  const hasCron = !!schedule.cronExpression;
  const hasEvents = Array.isArray(schedule.eventTriggers) && schedule.eventTriggers.length > 0;
  const hasDependency = !!schedule.dependsOnScheduleId;
  const runningRun = getRunningRun(runs, schedule.currentRunAgentSessionId);
  const liveDurationSeconds = getElapsedSeconds(runningRun?.createdAt ?? null, nowMs);
  const averageDurationSeconds = getAverageDurationSeconds(runs);

  return (
    <div
      onClick={() => onSelect(schedule)}
      className={`
        relative group cursor-pointer
        rounded-2xl border
        backdrop-blur-xl
        transition-all duration-300 ease-out
        hover:scale-[1.01] hover:shadow-xl
        ${visualStatus === 'running'
          ? 'border-[#FFB52B]/30 bg-white/[0.07] dark:bg-white/[0.04] shadow-[#FFB52B]/10 shadow-lg schedule-running-card'
          : visualStatus === 'active'
            ? 'border-white/[0.08] dark:border-white/[0.08] bg-white/90 dark:bg-white/[0.06] hover:border-white/[0.15] dark:hover:border-white/[0.15] hover:bg-white dark:hover:bg-white/[0.08]'
            : 'border-white/[0.05] dark:border-white/[0.05] bg-white/80 dark:bg-white/[0.03] opacity-70 hover:opacity-90'
        }
      `}
    >
      {/* Card content */}
      <div className="p-4 sm:p-5">
        {/* Header row: status dot + name + badges */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusDotClass(visualStatus)}`} />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {schedule.name}
            </h3>
            <span className="text-[9px] font-mono text-gray-400 dark:text-gray-600 hidden sm:inline" title={schedule.id}>
              {schedule.id.slice(0, 8)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`font-medium ${badge.className}`}>
              {badge.label}
            </span>
            {/* Actions menu */}
            <div className="relative">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                aria-label="Schedule actions menu"
                aria-expanded={showMenu}
                className="p-2 sm:p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
              >
                <MoreVertical className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-gray-400" />
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); }}
                  />
                  <div className="absolute right-0 top-full mt-1 z-50 w-40 rounded-xl border border-white/[0.1] dark:border-white/[0.1] bg-white/95 dark:bg-[#1a2332]/95 backdrop-blur-2xl shadow-2xl py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(false);
                        onToggleEnabled(schedule.id, schedule.enabled);
                      }}
                      className="w-full text-left px-3 py-2.5 sm:py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center gap-2"
                    >
                      {schedule.enabled ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      {schedule.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(false);
                        navigator.clipboard.writeText(schedule.id).then(() => {
                          setCopiedId(true);
                          setTimeout(() => setCopiedId(false), 2000);
                        }).catch(() => {});
                      }}
                      className="w-full text-left px-3 py-2.5 sm:py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center gap-2"
                    >
                      {copiedId ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                      {copiedId ? 'Copied!' : 'Copy ID'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(false);
                        onDelete(schedule.id);
                      }}
                      className="w-full text-left px-3 py-2.5 sm:py-1.5 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {schedule.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
            {schedule.description}
          </p>
        )}

        {/* Trigger info */}
        <div className="space-y-1.5 mb-3">
          {hasCron && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{cronToHuman(schedule.cronExpression)}</span>
            </div>
          )}
          {hasEvents && (
            <div className="flex items-center gap-1.5 text-xs">
              <Zap className="h-3 w-3 flex-shrink-0 text-amber-500" />
              <div className="flex flex-wrap gap-1">
                {(schedule.eventTriggers || []).map((evt) => (
                  <span
                    key={evt}
                    className="px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-500 dark:text-amber-400 border border-amber-500/20 text-[10px] font-medium"
                  >
                    {evt}
                  </span>
                ))}
              </div>
            </div>
          )}
          {hasDependency && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <GitBranch className="h-3 w-3 flex-shrink-0" />
              <span>Depends on another schedule</span>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 mb-3">
          <span title={exactDateTime(schedule.lastRunAt) || undefined}>Last: {timeAgo(schedule.lastRunAt)}{schedule.lastRunStatus ? ` (${schedule.lastRunStatus})` : ''}</span>
          <span className="text-gray-300 dark:text-gray-600 hidden sm:inline">|</span>
          <span>Next: {schedule.enabled ? timeUntil(schedule.nextRunAt) : '--'}</span>
          {liveDurationSeconds != null && (
            <>
              <span className="text-gray-300 dark:text-gray-600 hidden sm:inline">|</span>
              <span
                className="text-[#B57700] dark:text-[#FFCF74]"
                title={exactDateTime(runningRun?.createdAt) || 'Current run'}
              >
                Live: {formatDuration(liveDurationSeconds)}
              </span>
            </>
          )}
        </div>

        {/* Cost & run count */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 mb-3">
          <span>Runs: {schedule.runCount ?? 0}</span>
          {averageDurationSeconds != null && (
            <>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span>Avg: {formatDuration(averageDurationSeconds)}</span>
            </>
          )}
          {(schedule.totalCostUsd ?? 0) > 0 && (
            <>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span>Cost: ${(schedule.totalCostUsd ?? 0).toFixed(2)}</span>
            </>
          )}
          {(schedule.consecutiveFailures ?? 0) > 0 && (
            <>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span className="text-red-500 dark:text-red-400">
                {schedule.consecutiveFailures} failure{(schedule.consecutiveFailures ?? 0) > 1 ? 's' : ''}
              </span>
            </>
          )}
        </div>

        {/* Run history orbs */}
        <div className="min-w-0">
          <RunHistoryOrbs runs={runs} maxVisible={8} onClickRun={onClickRun} />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-white/[0.06]">
          {visualStatus === 'running' ? (
            <button
              type="button"
              onClick={handleStop}
              disabled={isActioning || stopping}
              aria-label={stopping ? `Stopping schedule ${schedule.name}` : `Stop schedule ${schedule.name}`}
              className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium
                bg-[#FFB52B]/15 text-[#FFB52B] hover:bg-[#FFB52B]/25 border border-[#FFB52B]/30
                transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
            >
              {(isActioning || stopping) ? <Loader2 className="h-3.5 w-3.5 sm:h-3 sm:w-3 animate-spin" /> : <Square className="h-3.5 w-3.5 sm:h-3 sm:w-3" />}
              {stopping ? 'Stopping...' : 'Stop'}
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRunNow(schedule.id); }}
              disabled={isActioning || !schedule.enabled}
              aria-label={`Run schedule ${schedule.name} now`}
              className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium
                bg-[#FFB52B]/15 text-[#FFB52B] hover:bg-[#FFB52B]/25 border border-[#FFB52B]/20
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
            >
              {isActioning ? <Loader2 className="h-3.5 w-3.5 sm:h-3 sm:w-3 animate-spin" /> : <Play className="h-3.5 w-3.5 sm:h-3 sm:w-3" />}
              Run Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleCard;
